import { Module, Mutation, VuexModule, Action } from 'vuex-module-decorators';

import container from '@/container';
import {
    FetchServiceContract,
    CONTRACTS,
    EventsServiceContract,
    Events
} from '@/core/contracts';
import { Context } from '@/dto';
import { RoleState, SettingsState } from '@/store';

const fetchService = container.get<FetchServiceContract>(
    CONTRACTS.FetchService
);
const eventsService = container.get<EventsServiceContract>(
    CONTRACTS.EventsService
);

export interface ContextsState {
    list: Context[];
    current?: Context;
}

@Module({ name: 'contexts', namespaced: true })
export default class ContextsModule extends VuexModule
    implements ContextsState {
    private _list: Context[] = [];
    private _current?: Context = undefined;

    public get list() {
        return this._list;
    }
    public get current() {
        return this._current;
    }
    public get currentContextId() {
        return this._current ? this._current.id : undefined;
    }

    @Action
    public async fetchContexts() {
        const contexts = await fetchService.get<Context[]>('/contexts/');
        this.setContexts(contexts);

        const current = contexts.find(context => context.current === true);
        if (current) {
            this.changeContext(current);
        }
    }

    @Action({ rawError: true })
    public async changeContext(payload: Context) {
        if (this.currentContextId !== payload.id) {
            this.setCurrentContext(payload);
            await fetchService.post('/contexts/current', payload.id);
            await SettingsState.fetchSettings();
            await RoleState.fetchRole({ contextId: payload.id });
            eventsService.raise(Events.ContextChanged, {
                contextId: payload.id
            });
        }
    }

    @Action
    public async changeContextName({ id, name }: { id: string; name: string }) {
        if (name) {
            name = name.trim();

            if (name !== '') {
                await fetchService!.post(`/contexts/${id}/name`, name);
                this.setContextName({ id, name });
            }
        }
    }

    @Action
    public async createContext(payload: string) {
        await fetchService.post('/contexts', payload);
        await this.fetchContexts();
    }

    @Mutation
    public updateTerminated({
        terminated,
        ContextID
    }: {
        terminated: boolean;
        ContextID: number;
    }) {
        const contexts = this._list.filter(r => Number(r.id) === ContextID);
        contexts.forEach(context => {
            context.terminated = terminated;
        });
    }

    @Mutation
    private setContexts(payload: Context[]) {
        this._list = payload;
    }

    @Mutation
    private setCurrentContext(payload: Context) {
        this._current = payload;
    }

    @Mutation
    private setContextName({ id, name }: { id: string; name: string }) {
        const context = this._list.filter(x => x.id === id)[0];

        if (context) {
            context.name = name;
        }
    }
}
